import { createFileRoute, useParams, useLocation } from '@tanstack/react-router';
import { Outlet, Link } from '@/lib/router-components';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import type { CourseDto, CourseStepDto, UserCourseProgressDto } from '@prometeus/contracts';

export const Route = createFileRoute('/certificacao/cursos/$courseId')({
  component: CursoLayout,
});

function CursoLayout() {
  const { courseId } = useParams({ from: '/certificacao/cursos/$courseId' });
  const location = useLocation();
  const pathname = location.pathname;
  const stateWorkspaceId = (location.state as { fromWorkspace?: boolean; workspaceId?: string })?.workspaceId;
  const storedWorkspaceId = (() => {
    try {
      return sessionStorage.getItem('certificationFromWorkspaceId');
    } catch {
      return null;
    }
  })();
  const workspaceId = stateWorkspaceId ?? storedWorkspaceId;
  const fromWorkspace = Boolean(workspaceId);

  const { data: courseData, isLoading } = useQuery({
    queryKey: ['certification-course', courseId],
    queryFn: async (): Promise<CourseDto & { steps: CourseStepDto[] }> => {
      const response = await api.get(`/certification/courses/${courseId}`);
      return response.data?.data ?? response.data;
    },
    enabled: !!courseId,
  });

  const { data: progress } = useQuery<UserCourseProgressDto | null>({
    queryKey: ['certification-progress', courseId],
    queryFn: async () => {
      try {
        const response = await api.get(`/certification/courses/${courseId}/progress`);
        return response.data?.data ?? response.data;
      } catch {
        return null;
      }
    },
    enabled: !!courseId,
  });

  const course = courseData;
  const steps = course?.steps ?? [];
  const progressPercent = progress?.progressPercent ?? 0;
  const isSimuladoPath = pathname.includes('/simulado');
  const isEtapaPath = pathname.includes('/etapa/');

  if (isLoading || !course) {
    return (
      <div className="p-6 max-w-6xl mx-auto" style={{ fontFamily: 'Poppins, sans-serif' }}>
        <div className="animate-pulse h-8 w-48 bg-[#9BA8AB]/30 dark:bg-gray-700 rounded-2xl mb-6" />
        <div className="flex gap-6">
          <div className="w-64 h-96 bg-[#9BA8AB]/20 dark:bg-gray-700 rounded-2xl animate-pulse" />
          <div className="flex-1 h-96 bg-[#9BA8AB]/20 dark:bg-gray-700 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="mb-6">
        <Link
          to={(fromWorkspace && workspaceId ? `/w/${workspaceId}/certificacoes` : '/certificacao') as any}
          className="text-sm text-[#9BA8AB] hover:text-[#06141B] dark:hover:text-white"
        >
          ← Voltar aos cursos
        </Link>
        {course.bannerUrl ? (
          <div
            className="mt-4 w-full rounded-[10px] overflow-hidden"
            style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
          >
            <div
              className="w-full h-32 sm:h-40 bg-[#06141B] flex items-center justify-center relative overflow-hidden"
              style={{ minHeight: 120 }}
            >
              <img
                src={course.bannerUrl}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-70"
              />
            </div>
            <div className="w-full h-1.5 bg-[#E5E7EB] dark:bg-gray-600 overflow-hidden">
              <div
                className="h-full bg-[#06141B] dark:bg-[#11212D] rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="mt-4 w-full h-1.5 rounded-full bg-[#E5E7EB] dark:bg-gray-600 overflow-hidden">
            <div
              className="h-full bg-[#06141B] dark:bg-[#11212D] rounded-full transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
        <p className="text-xs text-[#9BA8AB] mt-1 mb-4">{progressPercent}% concluído</p>
        <h1 className="text-base font-semibold text-[#06141B] dark:text-white mt-2">{course.title}</h1>
        {course.estimatedHours != null && (
          <p className="text-[#9BA8AB] mt-0.5" style={{ fontSize: 14 }}>
            {course.estimatedHours} {course.estimatedHours === 1 ? 'hora' : 'horas'}
          </p>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-72 flex-shrink-0">
          <nav
            className="rounded-[10px] bg-white dark:bg-gray-800 p-4"
            style={{
              fontFamily: "'Poppins', sans-serif",
              boxShadow: '0 0 15px rgba(0,0,0,0.05)',
            }}
          >
            <h2
              className="font-semibold mb-3 text-[#06141B] dark:text-white"
              style={{ fontSize: 16 }}
            >
              Módulos
            </h2>
            <ul className="space-y-1">
              {steps.map((step, index) => {
                const stepPath = `/certificacao/cursos/${courseId}/etapa/${step.id}`;
                const isActive = pathname === stepPath;
                const isCompleted = progress?.completedStepIds?.includes(step.id);
                return (
                  <li key={step.id}>
                    <Link
                      to={stepPath}
                      className={`flex items-center gap-2 px-2 py-2 rounded-md transition-colors ${
                        isActive
                          ? 'bg-blue-100 dark:bg-blue-900/30'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <img
                        src={isCompleted ? '/concluido.svg' : '/afazer.svg'}
                        alt={isCompleted ? 'Concluído' : 'A fazer'}
                        className="w-6 h-6 flex-shrink-0"
                      />
                      <span
                        className={`min-w-0 truncate text-[#06141B] dark:text-gray-200 ${isActive ? 'font-medium' : ''}`}
                        style={{ fontSize: 14 }}
                      >
                        Módulos: {step.title}
                      </span>
                    </Link>
                  </li>
                );
              })}
              <li className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                {progress?.status === 'certified' && progress?.certificateId ? (
                  <Link
                    to="/certificacao/certificados/$id"
                    params={{ id: progress.certificateId }}
                    className="flex items-center gap-2 px-2 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <img src="/concluido.svg" alt="" className="w-6 h-6 flex-shrink-0" />
                    <span className="text-[#06141B] dark:text-gray-200" style={{ fontSize: 14 }}>Ver certificado</span>
                  </Link>
                ) : progress?.status === 'certified' && !progress?.certificateId && course?.workspaceId ? (
                  <span className="flex items-center gap-2 px-2 py-2 rounded-md text-[#06141B] dark:text-gray-200" style={{ fontSize: 14 }}>
                    <img src="/concluido.svg" alt="" className="w-6 h-6 flex-shrink-0" />
                    Concluído
                  </span>
                ) : (
                  (() => {
                    const canDoSimulado =
                      progress?.status === 'all_steps_done' ||
                      progress?.status === 'eligible_for_quiz' ||
                      progress?.status === 'certified';
                    const simuladoContent = (
                      <>
                        <img
                          src="/afazer.svg"
                          alt=""
                          className={`w-6 h-6 flex-shrink-0 ${!canDoSimulado ? 'opacity-50' : ''}`}
                        />
                        <span
                          className={canDoSimulado ? 'text-[#06141B] dark:text-gray-200' : 'text-[#9BA8AB]'}
                          style={{ fontSize: 14 }}
                        >
                          Simulado
                        </span>
                      </>
                    );
                    if (canDoSimulado) {
                      return (
                        <Link
                          to="/certificacao/cursos/$courseId/simulado"
                          params={{ courseId }}
                          className={`flex items-center gap-2 px-2 py-2 rounded-md transition-colors ${
                            isSimuladoPath
                              ? 'bg-blue-100 dark:bg-blue-900/30'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          {simuladoContent}
                        </Link>
                      );
                    }
                    return (
                      <span
                        className="flex items-center gap-2 px-2 py-2 rounded-md cursor-not-allowed"
                        title="Complete todas as etapas anteriores para acessar o simulado"
                      >
                        {simuladoContent}
                      </span>
                    );
                  })()
                )}
              </li>
            </ul>
          </nav>
        </aside>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
